/**
 * The words on the character-file panel, and the one warning about storage.
 *
 * Chrome, and the sharpest case for why chrome is its own register: a player whose character will
 * not import needs a sentence they can act on, and a joke there costs them the character. See
 * [src/text/AGENTS.md](./AGENTS.md).
 *
 * Only the panel's own sequence lives here. Why a save was refused comes from
 * `@/save/saveFailureText`, which has a sentence for every refusal and an exhaustive switch that
 * makes a new one fail to compile rather than print nothing.
 */

export const SAVE_EXPORT = 'Export save';
export const SAVE_IMPORT = 'Import save';
export const SAVE_REPLACE = 'Replace';
export const SAVE_ADD = 'Import';
export const SAVE_CANCEL = 'Cancel';

export function saveExported(fileName: string): string {
  return `Exported to ${fileName}.`;
}

export function saveImported(
  name: string,
  backupFileName: string,
): { message: string; advice: string } {
  return {
    message: `Imported ${name}.`,
    advice: `Your previous character was downloaded as ${backupFileName}.`,
  };
}

/**
 * What the confirm row says before anything is written. Names the character being lost, because
 * that is the fact the player is being asked to weigh, and says the backup happens first.
 */
export function saveReplaceWarning(existingName: string): string {
  return `This replaces ${existingName}. Your current character downloads as a backup first.`;
}

export function saveAddWarning(incomingName: string): string {
  return `This adds ${incomingName} and plays as them. Your current character downloads first.`;
}

export const SAVE_CHECKED = 'Read and checked. Nothing is stored yet.';
export const SAVE_CANCELLED = 'Import cancelled. Nothing was changed.';
export const SAVE_NO_FILE = 'No file picked. Nothing was changed.';

/** Every failure below carries advice: a dead end with no next step is not worth printing. */
export const SAVE_DOWNLOAD_REFUSED = {
  message: 'This browser would not start the download.',
  advice: 'Check that downloads are allowed.',
} as const;

export const SAVE_UNREADABLE_FILE = {
  message: 'That file could not be read.',
  advice: 'Try picking it again.',
} as const;

export const SAVE_BACKUP_REFUSED = {
  message: 'The backup would not download, so nothing was imported.',
  advice: 'Allow downloads.',
} as const;

/**
 * Shown at the shell when storage is refused outright, which makes every other word in this file
 * moot: nothing will survive a reload, so the player is told before they spend an evening on it.
 */
export const SAVE_NO_STORAGE = {
  message: 'This browser is not storing your character.',
  advice: 'Nothing you do will survive a reload. Private browsing usually causes this.',
} as const;
